import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLead } from "./LeadDialog";

const HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_proturf-flagship/artifacts/b5ntw1ta_newnavig_.mp4";
const HERO_POSTER =
  "https://static.prod-images.emergentagent.com/jobs/e9dc7377-0248-4f5e-98bd-812b53b60fbf/images/bac709122a547f8cc7313740fea9bffcecc213bb581165c282353c898ade829b.png";

export const Hero = () => {
  const { openLead } = useLead();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [autoHidden, setAutoHidden] = useState(false);
  const [hovered, setHovered] = useState(false);
  const contentVisible = hovered || !autoHidden;

  useEffect(() => {
    const t = setTimeout(() => setAutoHidden(true), 10000);
    return () => clearTimeout(t);
  }, []);

  const loopEarly = (e) => {
    const v = e.currentTarget;
    if (v.duration && v.currentTime >= v.duration - 10) {
      v.currentTime = 0;
    }
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative min-h-screen overflow-hidden grain"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <video
          data-testid="hero-video"
          className="hidden sm:block h-full w-full object-cover"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay muted loop playsInline
          onTimeUpdate={loopEarly}
        />
        <video
          data-testid="hero-video-mobile"
          className="block sm:hidden h-full w-full object-cover"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay muted loop playsInline
          onTimeUpdate={loopEarly}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-[#0A0A0A]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 to-transparent" />

      <motion.div style={{ opacity }} className="relative z-10 flex min-h-screen flex-col justify-end px-6 md:px-12 lg:px-20 pb-24 pt-32">
        <div
          data-testid="hero-content"
          className={`transition-opacity duration-700 ${contentVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#D62828]">
          The Flagship Autonomous Mower
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="font-heading font-black uppercase leading-[0.85] tracking-normal text-white mt-4 text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]">
          Navigator
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="mt-6 max-w-xl text-base md:text-lg text-gray-300 leading-relaxed">
          Precision autonomous turf maintenance engineered for the world's most demanding grounds. RTK-GPS accurate. LiDAR safe. Always working.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
          className="mt-10 flex flex-col sm:flex-row gap-4">
          <button data-testid="hero-demo-btn" onClick={() => openLead("demo")}
            className="bg-[#D62828] text-white hover:bg-[#B91C1C] px-9 py-4 text-sm font-bold uppercase tracking-widest transition-all shadow-[0_0_25px_rgba(214,40,40,0.5)] hover:shadow-[0_0_35px_rgba(214,40,40,0.7)]">
            Book a Demo
          </button>
          <button data-testid="hero-quote-btn" onClick={() => openLead("quote")}
            className="border border-white/40 text-white hover:bg-white hover:text-black px-9 py-4 text-sm font-bold uppercase tracking-widest transition-colors backdrop-blur-sm">
            Request a Quote
          </button>
        </motion.div>
        </div>
      </motion.div>

      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50"
        animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
        <ArrowDown size={22} />
      </motion.div>
    </section>
  );
};
